import { getOnStorage } from '@/shared';
import { useUserContext } from './userContext/UserContext';
import { useNetworkStatusContext } from './NetworkStatusContext';
import { createContext, useState, useContext, useMemo, useCallback } from 'react';

type TimeMonitorContextType = {
    showSameDayModal?: boolean;
    validateLoginDate: () => Promise<void>;
    setShowSameDayModal: React.Dispatch<React.SetStateAction<boolean>>;
    showManualTimeChangeModal: boolean;
    setShowManualTimeChangeModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const TimeMonitorContext = createContext<TimeMonitorContextType>({} as TimeMonitorContextType);

const TimeMonitorProvider: React.FC<any> = ({ children }) => {
    const { isSignedIn } = useUserContext();
    const { connectivityStatus } = useNetworkStatusContext();

    const [showSameDayModal, setShowSameDayModal] = useState(false);
    const [showManualTimeChangeModal, setShowManualTimeChangeModal] = useState(false);

    const validateLoginDate = useCallback(async () => {
        const lastLoginStorage = getOnStorage('login_date');
        const lastLoginDate = new Date(lastLoginStorage!);

        const isOnline = connectivityStatus !== 'offline';
        const isVisible = document.visibilityState !== 'visible';

        if (isVisible || !isSignedIn || !isOnline) return

        const currentTime = new Date();
        const timeDifference = currentTime.getDate() != lastLoginDate.getDate();
        
        if (timeDifference) {
            setShowSameDayModal(true);
        }
    }, [isSignedIn, connectivityStatus]);

    const contextValue = useMemo(() => {
        return {
            showSameDayModal,
            showManualTimeChangeModal,
            validateLoginDate,
            setShowSameDayModal,
            setShowManualTimeChangeModal
        };
    }, [showManualTimeChangeModal, showSameDayModal, validateLoginDate]);

    return (
        <TimeMonitorContext.Provider value={contextValue}>
            {children}
        </TimeMonitorContext.Provider>
    );
};

const useTimeMonitorContext = () => {
    const ctx = useContext(TimeMonitorContext);

    if (!ctx) {
        throw new Error('useTimeMonitor must be used within a TimeMonitorProvider');
    }

    return ctx;
};

export { TimeMonitorProvider, useTimeMonitorContext };
